import type { IncomingMessage, OutgoingMessage } from 'http';
import type { AuthClientLike } from '@dx-ui/framework-auth-provider';
import { mergeCacheControl } from '@dx-ui/framework-merge-cache-control';
import type {
  MutationFunction,
  QueryFunction,
  QueryFunctionContext,
  QueryKey,
} from '@tanstack/react-query';
import type { OneLinkConfig } from '@dx-ui/framework-i18n';
import { isInOneLinkConfig } from '@dx-ui/framework-i18n';
import {
  causedByForbidden,
  causedByUnauthorized,
  generateReferrer,
  getSafeLanguage,
  getVisitorId,
} from './default-fetch-fn-utils';
import { extractFiles, isExtractableFile } from './extract-files';
import FormData from 'form-data';
import { logWarning, logError } from '@dx-ui/framework-logger';
import { isBrowser } from '@dx-ui/utilities-is-browser';
import type {
  QueryRequestArgs,
  DXExecutionResult,
  QueryDebugInfo,
  QueryKeyArgs,
} from '@dx-ui/types-graphql';
import { ServerAuthError, GraphError } from '@dx-ui/types-graphql';
import { generateError } from '@dx-ui/utilities-generate-error';
import type { errors } from 'undici';
import { isQueryIncomplete, triggerInCompleteEvent } from '@dx-ui/framework-incomplete-event';
import { filterCookies } from './cookie-forward';
import { env } from '@dx-ui/framework-env';
import Cookies from 'universal-cookie';

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: QueryMeta;
  }
}

export type QueryMeta = {
  variablesToIncludeInParams?: string[];
  validateTokenBeforeRequest?: boolean;
};

type QueryRequest = QueryRequestArgs & MakeDefaultFnArgs & { meta?: QueryMeta };

type QueryResult = DXExecutionResult['data'];

const forwardHeaders = ['dx-preview', 'dx-toggles'];

export async function makeRequest(
  queryRequest: QueryRequest,
  signal?: AbortSignal,
  retries = 0,
  undiciErrorSocketRetries = 0
): Promise<QueryResult> {
  const {
    appName,
    authClient,
    authenticatedOperationNames,
    handleAuthenticatedOperationError,
    handleRequestEndpoint,
    handleRequestHeaders,
    customHeaders,
    document,
    meta,
    oneLinkConfig,
    referrer: referrerArg,
    referrerPolicy,
    routerLocale = '',
    serverRequest: serverRequestArg,
    serverResponse,
    ssrMode = false,
    variables,
    variablesToIncludeInParams,
  } = queryRequest;

  if (!authClient) {
    throw new Error('auth client not provided');
  }
  const appVersion = env('APP_VER', appName ? `${appName}:0.0.1` : '');
  const serverRequest = serverRequestArg || serverResponse?.req;
  const { operationString: query, operationName, originalOpName } = document;
  const requestBody = { query, operationName, variables };
  const authenticatedOperation = authenticatedOperationNames?.includes(operationName);
  const endpoint = handleRequestEndpoint?.(serverRequest) || authClient.getGqlEndpoint();
  let authHeader: string | null = null;
  const cookieHeader = serverRequest?.headers.cookie;
  const cookies = new Cookies(cookieHeader);
  authClient.webGuestMetadataRequestCookie = cookies.get('webGuestMetadata');

  if (meta?.validateTokenBeforeRequest) {
    await authClient.validateToken();
  }

  if (!authClient.webGuestMetadataRequestCookie && authClient.webGuestMetadataCookie) {
    await authClient.refreshToken();
    return makeRequest(queryRequest, signal, retries, undiciErrorSocketRetries);
  }

  if (authenticatedOperation && !authClient.getIsLoggedIn()) {
    const accessToken = await handleAuthenticatedOperationError?.().catch(() => null);
    authHeader = accessToken ? `Bearer ${accessToken}` : await authClient.getAuthorizationHeader();
  } else {
    authHeader = await authClient.getAuthorizationHeader();
  }

  const visitoridHeader = serverRequest?.headers['visitorid'] as string | undefined;
  const hltClientMessageIdHeader = serverRequest?.headers['hltclientmessageid'] as
    | string
    | undefined;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(!authClient.webGuestMetadataCookie && { Authorization: authHeader }),
    'dx-platform': (serverRequest?.headers['dx-platform'] as string) ?? 'web',
    ...(visitoridHeader && { visitorid: visitoridHeader }),
    ...(hltClientMessageIdHeader && { hltclientmessageid: hltClientMessageIdHeader }),
    ...customHeaders,
    ...handleRequestHeaders?.(serverRequest),
  };

  const bodyLanguage =
    (variables?.language as string | undefined) || (variables?.lang as string | undefined) || 'en';
  const browserUrl = isBrowser ? window.location.href : '';
  const includeLanguageParam = isInOneLinkConfig(
    routerLocale,
    appName || '',
    browserUrl,
    oneLinkConfig
  );

  if (ssrMode) {
    headers['dx-trusted-app'] = 'true';
  }

  // Always forward these headers from the incoming request
  forwardHeaders.forEach((header) => {
    if (serverRequest?.headers[header]) {
      headers[header] = serverRequest?.headers[header] as string;
    }
  });

  if (cookieHeader && authClient.webGuestMetadataRequestCookie?.webGuestTokenType !== 'app') {
    headers['cookie'] = filterCookies(cookieHeader, ['webGuestToken', 'webGuestMetadata']);
  }

  // Add generated visitorId & messsageId
  const visitorId = getVisitorId();
  if (visitorId || visitoridHeader) {
    const requestId = crypto.randomUUID().replace(/-/g, '');
    const hltClientMessageId = `${visitorId}-${requestId}`;
    //If custom header for visitorid or hltClientMessageid are set, use that instead of the generated one
    headers['visitorid'] = visitoridHeader || visitorId || '';
    headers['hltclientmessageid'] = hltClientMessageIdHeader || hltClientMessageId || '';
  }

  // Add referrer
  const referrer = referrerArg || generateReferrer(endpoint, routerLocale, serverRequest);

  // Create params from variables
  const mergedVariablesToIncludeInParams = [
    ...new Set([
      ...(variablesToIncludeInParams ?? []),
      ...(meta?.variablesToIncludeInParams ?? []),
    ]),
  ];
  const paramsFromVariables: Record<string, unknown> = {};
  if (mergedVariablesToIncludeInParams.length) {
    mergedVariablesToIncludeInParams.forEach((variable) => {
      const value = variables?.[variable];
      if (value) {
        paramsFromVariables[variable] = value;
      }
    });
  }

  // Add params
  const params = new URLSearchParams({
    appName,
    appVersion,
    operationName,
    originalOpName,
    bl: bodyLanguage,
    ...(includeLanguageParam && { language: getSafeLanguage(routerLocale) }),
    ...paramsFromVariables,
  }).toString();

  // Append files
  // https://github.com/jaydenseric/apollo-upload-client/blob/master/public/createUploadLink.js
  const { clone, files } = extractFiles(requestBody, '', isExtractableFile);
  const form = new FormData();
  if (files.size) {
    // Automatically set by `fetch` when the `body` is a `FormData` instance.
    delete headers['Content-Type'];

    // set apollo header for > Apollo Server 4 CSRF protection (must be after merging form headers)
    headers['x-apollo-operation-name'] = operationName;
    // GraphQL multipart request spec:
    // https://github.com/jaydenseric/graphql-multipart-request-spec
    form.append('operations', JSON.stringify(clone));

    const map: Record<number, string[]> = {};
    let i = 0;
    files.forEach((paths) => {
      map[++i] = paths;
    });
    form.append('map', JSON.stringify(map));

    i = 0;
    files.forEach((_, file: File) => {
      formDataAppendFile(form, ++i, file);
    });
  }

  const finalUrl = `${endpoint}?${params}`;

  let response: Response;
  try {
    response = await fetch(finalUrl, {
      method: 'POST',
      headers,
      referrer,
      referrerPolicy,
      body: files.size ? (form as unknown as BodyInit) : JSON.stringify(requestBody),
      signal,
    });
  } catch (error) {
    const safeError = generateError(error);
    if (
      (safeError as errors.SocketError).code === 'UND_ERR_SOCKET' &&
      undiciErrorSocketRetries < 1
    ) {
      return makeRequest(queryRequest, signal, retries, undiciErrorSocketRetries + 1);
    }
    throw error;
  }

  const refreshAndRetry = async () => {
    await authClient.refreshToken();
    return makeRequest(queryRequest, signal, retries + 1, undiciErrorSocketRetries);
  };
  if (!response?.ok) {
    if (typeof window === 'undefined') {
      // get hltclientmessageid and visitorid from headers if present
      const responseVisitorId = response?.headers.get('visitorid');
      const responseMessageId = response?.headers.get('hltclientmessageid');
      const colors = {
        red: '\x1b[31m',
        reset: '\x1b[0m',
      };
      logError(
        'FETCH_ERROR',
        new Error('Server side graphql request not ok'),
        `${colors.red}GraphQL request failed
status: ${response?.status}
text: ${response?.statusText}
operationName: ${operationName}
messageId: ${responseMessageId}
visitorId: ${responseVisitorId}${colors.reset}
`
      );
    }
    const responseText = await response?.text();
    let jsonResult: DXExecutionResult | undefined;
    try {
      jsonResult = JSON.parse(responseText);
    } catch {
      // ignore failed to parse json
    }

    const isForbidden = response?.status === 403;

    const referenceError = jsonResult?.errors?.map((er) => er['reference-error']);
    referenceError?.forEach((ref) => {
      if (ref) {
        logWarning('403', '403 reference', ref);
      }
    });

    if (authenticatedOperation) {
      if (!isForbidden && authClient.getIsLoggedIn() && retries < 1) {
        return refreshAndRetry();
      }
      const errorMessage = jsonResult?.errors?.[0]?.message || '';
      throw new ServerAuthError(response?.status, errorMessage);
    }

    const isUnauthorized = response?.status === 401;
    if (isUnauthorized && retries < 1) {
      return refreshAndRetry();
    }
    // handle errors if response is not ok
    const shouldRetry =
      jsonResult && handleResultErrors(jsonResult, authClient, authenticatedOperation, retries);
    if (shouldRetry) return refreshAndRetry();

    // request failed for unknown reason
    throw new Error(`request failed ${response?.status} ${responseText}`);
  }

  // merge cache headers if server
  if (response instanceof Response) {
    mergeCacheControl(response, serverResponse);
  }

  const result: DXExecutionResult = await response.json();

  // handle errors if response is ok
  const shouldRetry = handleResultErrors(result, authClient, authenticatedOperation, retries);
  if (shouldRetry) return refreshAndRetry();

  if (isQueryIncomplete(response)) {
    triggerInCompleteEvent({
      requestBody: {
        operationName,
        originalOpName,
      },
    });
  }

  return {
    ...result.data,
    __info__: {
      dxCompleteness: response.headers.get('dx-completeness'),
      isSSR: typeof window === 'undefined',
      operationName,
      originalOpName,
    },
  };
}

export type IncompleteQueryEvent = QueryDebugInfo & {
  message: string;
};

export type MakeDefaultFnOneLinkArgs =
  | {
      oneLinkConfig?: never;
      routerLocale?: never;
    }
  | {
      oneLinkConfig: OneLinkConfig | null;
      routerLocale: string;
    };

export type MakeDefaultFnAuthenticatedOperationErrorArgs =
  | {
      handleAuthenticatedOperationError: () => Promise<string | null>;
      authenticatedOperationNames: string[];
    }
  | {
      handleAuthenticatedOperationError?: never;
      authenticatedOperationNames?: never;
    };

export type MakeDefaultFnOptionalArgs = MakeDefaultFnOneLinkArgs &
  MakeDefaultFnAuthenticatedOperationErrorArgs;

export type MakeDefaultFnArgs = {
  appName: string;
  authClient: AuthClientLike;
  customHeaders?: Record<string, string>;
  customParams?: Record<string, string>;
  handleRequestEndpoint?: (req?: IncomingMessage) => string;
  handleRequestHeaders?: (req?: IncomingMessage) => Record<string, string> | undefined;
  referrer?: string;
  referrerPolicy?: ReferrerPolicy;
  serverRequest?: IncomingMessage;
  serverResponse?: OutgoingMessage;
  ssrMode?: boolean;
  variablesToIncludeInParams?: string[];
} & MakeDefaultFnOptionalArgs;

export function makeDefaultQueryFn(args: MakeDefaultFnArgs): QueryFunction<QueryResult, QueryKey> {
  return async ({
    queryKey,
    signal,
    meta,
  }: QueryFunctionContext<QueryKey>): Promise<QueryResult> => {
    const [query, variables] = queryKey as QueryKeyArgs;
    return makeRequest(
      {
        ...args,
        document: query,
        variables,
        meta: meta,
      },
      signal
    );
  };
}

export function makeDefaultMutationFn(args: MakeDefaultFnArgs): MutationFunction {
  return (params): Promise<unknown> => {
    const [mutation, variables] = params as QueryKeyArgs;
    return makeRequest({
      ...args,
      document: mutation,
      variables,
    });
  };
}

function formDataAppendFile(formData: FormData, fieldName: number, file: File) {
  formData.append(fieldName.toString(), file, file.name);
}

function handleResultErrors(
  result: DXExecutionResult,
  authClient: AuthClientLike,
  authenticatedOperation = false,
  retries: number
) {
  if (result.errors) {
    const error = new GraphError(result);

    if (authenticatedOperation) {
      if (
        (causedByUnauthorized(error) || causedByForbidden(error)) &&
        authClient.getIsLoggedIn() &&
        retries < 1
      ) {
        // return true to retry
        return true;
      }
    }
    throw error;
  }
  return false;
}
